'use client';

import * as React from 'react';

type Theme = 'light' | 'dark' | 'system' | string;

type ThemeContextValue = {
  theme: Theme | undefined;
  setTheme: (t: Theme) => void;
};

const ThemeContext = React.createContext<ThemeContextValue | undefined>(undefined);

function applyThemeToDocument(value: Theme | undefined, attribute = 'data-theme') {
  if (typeof window === 'undefined') return;
  const html = document.documentElement;
  if (!value) return;
  if (attribute === 'class') {
    // treat value as class name
    html.classList.remove('light', 'dark');
    html.classList.add(String(value));
  } else {
    html.setAttribute(attribute, String(value));
  }
}

export function ThemeProvider({
  children,
  attribute = 'data-theme',
  defaultTheme = 'light',
  enableSystem = true,
  storageKey = 'theme',
  // accept but ignore extra next-themes props
  disableTransitionOnChange,
  ...rest
}: {
  children: React.ReactNode;
  attribute?: string;
  defaultTheme?: Theme;
  enableSystem?: boolean;
  storageKey?: string;
  disableTransitionOnChange?: boolean;
  [key: string]: any;
}) {
  const [theme, setThemeState] = React.useState<Theme | undefined>(() => {
    if (typeof window === 'undefined') return undefined;
    try {
      const stored = localStorage.getItem(storageKey);
      if (stored) return stored as Theme;
    } catch (e) {
      /* ignore */
    }
    return defaultTheme;
  });

  React.useEffect(() => {
    if (!theme) return;
    if (theme === 'system' && enableSystem) {
      const mq = window.matchMedia('(prefers-color-scheme: dark)');
      const system = mq.matches ? 'dark' : 'light';
      applyThemeToDocument(system, attribute);
      const onChange = () => applyThemeToDocument(mq.matches ? 'dark' : 'light', attribute);
      mq.addEventListener('change', onChange);
      return () => mq.removeEventListener('change', onChange);
    }

    applyThemeToDocument(theme, attribute);
    try {
      localStorage.setItem(storageKey, String(theme));
    } catch (e) {
      /* ignore */
    }
  }, [theme, attribute, enableSystem, storageKey]);

  const setTheme = React.useCallback((t: Theme) => setThemeState(t), []);

  return <ThemeContext.Provider value={{ theme, setTheme }}>{children}</ThemeContext.Provider>;
}

export function useTheme() {
  const ctx = React.useContext(ThemeContext);
  if (ctx) return ctx;
  return { theme: undefined, setTheme: () => {} } as ThemeContextValue;
}
